import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReferralLetter, useReferringDoctors, usePatients, useVisit, usePatientVisits, useClinicDoctors, useAppSettings } from "@/lib/queries";
import { useCreateReferralLetter, useUpdateReferralLetter, useSendReferralWebhook, useAiPolish } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Send, Printer, Sparkles, WandSparkles, Upload } from "lucide-react";
import { DEFAULT_AI_CONTEXTS, getAiContext } from "@emr/shared/ai";
import { createReferralPdf } from "@/lib/pdf/referralPdf";
import { savePdfToR2 } from "@/lib/r2";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import { printPdf } from "@/lib/print";
import { printDoctorLabel } from "@/lib/printLabel";
import { toast } from "sonner";
import { fmtDate } from "@emr/shared/india";

// "Referral Reply" form — Incoming referral letter (specialist replying to the
// originating GP). letterType is fixed to "Incoming". This is a dedicated,
// self-contained copy of the original combined editor — context-specific labels,
// AI context, and fields. No Subject field (replies reference the originating
// doctor, not a subject line).

// SOAP note sections rendered with checkboxes in the AI Draft card — keys match
// EmrNote fields; labels match the EMR visit page (VisitColumns).
const SOAP_SECTIONS = [
  { key: "subjective", label: "Presenting Complaints" },
  { key: "objective", label: "Findings" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "plan", label: "Plan" },
] as const;

type SoapKey = (typeof SOAP_SECTIONS)[number]["key"];

export function NewReferralReply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const visitIdParam = searchParams.get("visitId");

  const { data: existing, isLoading } = useReferralLetter(id);
  const { data: doctorsData } = useReferringDoctors();
  const { data: clinicDoctorsData } = useClinicDoctors();
  const { data: settings } = useAppSettings();
  const doctors: any[] = (doctorsData as any)?.rows ?? (Array.isArray(doctorsData) ? doctorsData : []) ?? [];
  const clinicDoctors: any[] = (clinicDoctorsData as any)?.rows ?? (Array.isArray(clinicDoctorsData) ? clinicDoctorsData : []) ?? [];

  const [form, setForm] = useState({
    patientId: "" as string,
    patientQuery: "",
    visitId: (visitIdParam ?? "") as string,
    doctorId: "" as string,
    createdByDoctorId: "" as string,
    body: "" as string,
    aiContext: DEFAULT_AI_CONTEXTS.referralReply as string,
    plan: "" as string,
  });
  // Which visit SOAP sections are included in the AI payload (2026-10-08).
  // All ticked by default; untick a section to withhold it from the AI.
  const [soapSel, setSoapSel] = useState<Record<SoapKey, boolean>>({ subjective: true, objective: true, diagnosis: true, plan: true });
  const [updatedAt, setupdatedAt] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const create = useCreateReferralLetter();
  const update = useUpdateReferralLetter(id ?? "");
  const sendWebhook = useSendReferralWebhook();
  const polish = useAiPolish();

  const { data: patientsData } = usePatients(form.patientQuery, 1, 10);
  const patientOptions = patientsData?.rows ?? [];
  const patientSearchRef = useRef<HTMLInputElement>(null);
  const { highlight: ptHighlight, highlightRef: ptHighlightRef, onKeyDown: onPtSearchKeyDown } = useDropdownNav({
    count: form.patientQuery.length >= 3 ? patientOptions.length : 0,
    onSelect: (i) => {
      if (patientOptions[i]) {
        const p = patientOptions[i];
        setForm((f) => ({ ...f, patientId: p.id, patientQuery: [p.salutation, p.name].filter(Boolean).join(" ") + " · " + p.clinicUhid, visitId: "" }));
      }
    },
    inputRef: patientSearchRef,
  });
  const { data: visitData } = useVisit(form.visitId || undefined);
  // Visits for the currently-selected patient (for the Visit dropdown).
  const { data: patientVisitsData } = usePatientVisits(form.patientId || undefined);
  const patientVisits = patientVisitsData?.rows ?? [];

  // Load existing letter for editing.
  useEffect(() => {
    if (existing?.letter) {
      const l = existing.letter;
      // Only load if this is an Incoming letter — protect against a wrong-type edit URL.
      if (l.letterType !== "Incoming") {
        navigate(`/referrals/to/${l.id}`, { replace: true });
        return;
      }
      setForm({
        patientId: l.patientId,
        patientQuery: "",
        visitId: l.visitId ?? "",
        doctorId: l.doctorId ?? "",
        createdByDoctorId: l.createdByDoctorId ?? "",
        body: l.body ?? "",
        aiContext: l.aiContext ?? getAiContext("referralReply", settings?.aiPromptReferralReply),
        plan: "",
      });
      setupdatedAt(l.updatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is stable; settings is only for the aiContext fallback
  }, [existing]);

  // Sync the AI system prompt from clinic settings when they load (for new letters only,
  // not when editing an existing one — the saved aiContext is preserved above).
  useEffect(() => {
    if (!id && settings) {
      setForm((f) => ({ ...f, aiContext: getAiContext("referralReply", settings.aiPromptReferralReply) }));
    }
  }, [settings, id]);

  // When a patient is picked from the search dropdown (no visit chosen yet),
  // default the Visit selector to the most recent visit for that patient.
  useEffect(() => {
    if (form.patientId && !form.visitId && patientVisits.length > 0) {
      setForm((f) => ({ ...f, visitId: patientVisits[0].id }));
    }
    if (!form.patientId && form.visitId) {
      setForm((f) => ({ ...f, visitId: "" }));
    }
  }, [form.patientId, patientVisits]);

  // When a visit is loaded (either from URL or dropdown), sync the patient id,
  // the patient display field, and the signatory doctor from the visit. The body
  // is NOT auto-filled from the SOAP note anymore (2026-10-08): the visit's SOAP
  // notes are shown checkbox-gated in the AI Draft card and the letter body is
  // produced by "Generate AI draft" (or typed manually).
  useEffect(() => {
    if (visitData?.visit) {
      const v = visitData.visit;
      setForm((f) => ({
        ...f,
        patientId: v.patientId,
        patientQuery: f.patientQuery || [v.patientName ? v.patientName : "", `UHID ${v.clinicUhid ?? ""}`].filter(Boolean).join(" · "),
        doctorId: f.doctorId,
        createdByDoctorId: v.doctorId ?? f.createdByDoctorId,
      }));
    }
  }, [visitData]);

  const webhookConfigured = settings?.referralReplyWebhookEnabled && settings?.referralReplyWebhookUrl;

  // Gather referring-doctor, signatory (replying clinic doctor), and clinic details
  // so the AI can address the letter to the right doctor and sign it with the right
  // credentials. The plan field comes from the "Doctor's input" textarea in the
  // AI Draft card.
  const buildLetterContext = () => {
    const doctor = doctors.find((d: any) => d.id === form.doctorId);
    const signatory = clinicDoctors.find((d: any) => d.id === form.createdByDoctorId);
    return {
      referringDoctor: doctor ? {
        name: doctor.name,
        gender: doctor.gender,
        designation: doctor.designation,
        clinicHospital: doctor.clinicHospital,
        address: doctor.address,
      } : null,
      replyingDoctor: signatory ? {
        name: signatory.name,
        qualifications: signatory.qualifications,
        regNumber: signatory.regNumber,
        specialty: signatory.specialty,
      } : null,
      clinic: settings ? {
        name: settings.clinicName,
        address: settings.address,
        phone: settings.phone,
      } : null,
      plan: form.plan.trim() || null,
    };
  };

  const doGenerateDraft = async () => {
    if (!form.body.trim() && !visitData?.note) {
      toast.info("Nothing to generate from — add visit details or a draft first.");
      return;
    }
    // Build the full AI payload: letter context (referring doctor, replying doctor,
    // clinic, plan) + the visit's clinical fields — only the SOAP sections ticked
    // in the AI Draft card are included. Note: visitData.note.plan is the visit's
    // SOAP plan; form.plan is the user-entered "Doctor's input" field for the reply.
    const ctx = buildLetterContext();
    const note = visitData?.note;
    const userMessage = note
      ? JSON.stringify({
          ...ctx,
          patient: visitData.visit.patientName,
          uhid: visitData.visit.clinicUhid,
          presentingComplaints: soapSel.subjective ? note.subjective : undefined,
          findings: soapSel.objective ? note.objective : undefined,
          diagnosis: soapSel.diagnosis ? note.diagnosis : undefined,
          visitPlan: soapSel.plan ? note.plan : undefined,
        })
      : JSON.stringify({ ...ctx, draftText: form.body });
    try {
      const res = await polish.mutateAsync({ text: userMessage, aiContext: form.aiContext });
      setForm((f) => ({ ...f, body: res.text }));
      toast.success("Draft generated");
    } catch { /* handled */ }
  };

  const doPolish = async () => {
    if (!form.body.trim()) { toast.info("Nothing to polish."); return; }
    try {
      const res = await polish.mutateAsync({
        text: JSON.stringify({ ...buildLetterContext(), textToPolish: form.body }),
        aiContext: form.aiContext,
      });
      setForm((f) => ({ ...f, body: res.text }));
      toast.success("Polished");
    } catch { /* handled */ }
  };

  const buildPdfData = () => {
    const letter = existing?.letter;
    return {
      letterType: "Incoming" as const,
      patientName: letter?.patientName ?? (() => { const p: any = patientOptions.find((p: any) => p.id === form.patientId); return p ? [p.salutation, p.name].filter(Boolean).join(" ") : "Patient"; })(),
      clinicUhid: letter?.clinicUhid ?? patientOptions.find((p: any) => p.id === form.patientId)?.clinicUhid ?? null,
      patientDob: letter?.patientDob ?? null,
      patientGender: letter?.patientGender ?? null,
      doctorName: doctors.find((d: any) => d.id === form.doctorId)?.name ?? null,
      doctorGender: doctors.find((d: any) => d.id === form.doctorId)?.gender ?? null,
      doctorDesignation: doctors.find((d: any) => d.id === form.doctorId)?.designation ?? null,
      doctorClinic: doctors.find((d: any) => d.id === form.doctorId)?.clinicHospital ?? null,
      doctorAddress: doctors.find((d: any) => d.id === form.doctorId)?.address ?? null,
      subject: null,
      body: form.body || null,
      signatoryName: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.name ?? null,
      signatoryQualifications: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.qualifications ?? null,
      signatoryRegNumber: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.regNumber ?? null,
      date: new Date().toISOString(),
    };
  };

  // Save the letter to the DB (create or update). Returns the saved letter id
  // (or null on failure). This is the base action — the other actions build on top.
  const saveToDb = async (): Promise<string | null> => {
    if (!form.patientId) { toast.error("Select a patient first."); return null; }
    const payload: Record<string, unknown> = {
      patientId: form.patientId,
      visitId: form.visitId || null,
      doctorId: form.doctorId || null,
      createdByDoctorId: form.createdByDoctorId || null,
      letterType: "Incoming",
      subject: null,
      body: form.body || null,
      aiContext: form.aiContext || null,
    };
    let savedId = id;
    if (id) {
      payload.updatedAt = updatedAt;
      const res = await update.mutateAsync(payload);
      setupdatedAt((res as any)?.updatedAt);
    } else {
      const res = await create.mutateAsync(payload);
      savedId = (res as any)?.id;
      if (savedId) navigate(`/referrals/reply/${savedId}`, { replace: true });
    }
    return savedId ?? null;
  };

  // Generate the PDF, upload it to R2, and mark the letter as savedToR2.
  const saveToR2 = async (savedId: string) => {
    const doc = await createReferralPdf(buildPdfData());
    const patient = patientOptions.find((p: any) => p.id === form.patientId) ?? existing?.letter as any;
    const fullName = patient?.name ? [patient?.salutation, patient?.name].filter(Boolean).join(" ") : (patient?.patientName ?? "Patient");
    const fileName = `${fullName}_${fmtDate(new Date()).replace(/\//g, "-")}_${patient?.clinicUhid ?? "UHID"}_${savedId.slice(0, 6)}`;
    const r2Key = await savePdfToR2(doc, "referrals", fileName, form.patientId, form.visitId || undefined);
    await update.mutateAsync({ savedToR2: true, r2Key: r2Key });
  };

  // --- Standalone action handlers ---

  const handleSave = async () => {
    setBusy(true);
    try {
      await saveToDb();
    } finally {
      setBusy(false);
    }
  };

  const handleSaveToR2 = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (!savedId) return;
      await saveToR2(savedId);
      toast.success("Saved + uploaded to R2");
    } catch {
      toast.error("PDF generation/R2 upload failed — letter saved without PDF.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (!savedId) return;
      const doc = await createReferralPdf(buildPdfData());
      printPdf(doc);
    } catch {
      toast.error("PDF generation failed — could not print.");
    } finally {
      setBusy(false);
    }
  };

  const handleSendWebhook = async () => {
    setBusy(true);
    try {
      const savedId = await saveToDb();
      if (!savedId) return;
      await sendWebhook.mutateAsync(savedId);
    } finally {
      setBusy(false);
    }
  };

  const selectedDoctor = doctors.find((d: any) => d.id === form.doctorId);
  const selectedVisit = patientVisits.find((v: any) => v.id === form.visitId);
  const selectedVisitDiagnosis = selectedVisit?.diagnosis ?? visitData?.note?.diagnosis ?? null;
  // The selected visit's SOAP note — shown (checkbox-gated) in the AI Draft card.
  const visitNote = visitData?.note ?? null;

  if (isLoading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/referrals")}><ArrowLeft size={18} /></Button>
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">{id ? "Edit Referral Reply" : "New Referral Reply"}</h1>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Referral Reply — Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label>Patient *</Label>
            <Input
              ref={patientSearchRef}
              value={form.patientQuery}
              onChange={(e) => setForm({ ...form, patientQuery: e.target.value, patientId: "", visitId: "" })}
              onKeyDown={onPtSearchKeyDown}
              placeholder="Type name/phone/UHID to search (3+ chars)…"
            />
            {patientOptions.length > 0 && (
              <div className="visible-scrollbar mt-1 max-h-40 overscroll-contain overflow-auto rounded border border-cream-200 bg-cream-100">
                {patientOptions.map((p: any, idx: number) => (
                  <button
                    key={p.id}
                    type="button"
                    ref={idx === ptHighlight ? ptHighlightRef : undefined}
                    className={`block w-full px-3 py-1 text-left text-sm hover:bg-cream-200 ${
                      idx === ptHighlight ? "bg-cream-200" : ""
                    }`}
                    onClick={() => { setForm((f) => ({ ...f, patientId: p.id, patientQuery: [p.salutation, p.name].filter(Boolean).join(" ") + " · " + p.clinicUhid, visitId: "" })); }}
                  >
                    {[p.salutation, p.name].filter(Boolean).join(" ")} · {p.clinicUhid} · {p.phone}
                  </button>
                ))}
              </div>
            )}
            {form.patientId && <p className="mt-1 text-xs text-green-700">Selected: {form.patientId.slice(0, 8)}…</p>}
          </div>
          <div>
            <Label>From (referring doctor)</Label>
            <Select value={form.doctorId} onValueChange={(v) => setForm({ ...form, doctorId: v })}>
              <SelectTrigger><SelectValue placeholder="Select referring doctor" /></SelectTrigger>
              <SelectContent>
                {doctors.map((d: any) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.designation}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Signatory (clinic doctor)</Label>
            <Select value={form.createdByDoctorId} onValueChange={(v) => setForm({ ...form, createdByDoctorId: v })}>
              <SelectTrigger><SelectValue placeholder="Select clinic doctor" /></SelectTrigger>
              <SelectContent>
                {clinicDoctors.map((d: any) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visit {form.patientId && patientVisits.length === 0 ? "(no visits found)" : ""}</Label>
            <Select
              value={form.visitId}
              onValueChange={(v) => setForm({ ...form, visitId: v })}
              disabled={!form.patientId || patientVisits.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={form.patientId ? "Select a visit" : "Select a patient first"} />
              </SelectTrigger>
              <SelectContent>
                {patientVisits.map((v: any) => (
                  <SelectItem key={v.id} value={v.id}>
                    {fmtDate(new Date(v.checkInAt))} · {v.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.visitId && (
              <p className="mt-1 text-xs"><span className="font-medium">Diagnosis:</span> {selectedVisitDiagnosis ?? "No diagnosis"}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><WandSparkles size={16} /> AI Draft</CardTitle></CardHeader>
        <CardContent>
          <div className={form.visitId ? "grid grid-cols-1 gap-4 md:grid-cols-2" : ""}>
            {/* Left — the selected visit's SOAP notes, checkbox-gated (2026-10-08):
                ticked sections are included in the AI payload; unticked are withheld. */}
            {form.visitId && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted">Visit SOAP notes</Label>
                {visitData === undefined ? (
                  <Skeleton className="h-40" />
                ) : visitNote ? (
                  SOAP_SECTIONS.map((s) => (
                    <div key={s.key} className="rounded-md border border-cream-200 p-2 dark:border-dark-700">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`soap-${s.key}`}
                          checked={soapSel[s.key]}
                          onCheckedChange={(v) => setSoapSel({ ...soapSel, [s.key]: v === true })}
                        />
                        <Label htmlFor={`soap-${s.key}`} className="text-sm font-semibold">{s.label}</Label>
                      </div>
                      <p
                        className={`visible-scrollbar mt-1 max-h-24 overflow-auto whitespace-pre-wrap text-sm ${
                          soapSel[s.key] ? "text-ink dark:text-cream-50" : "text-muted line-through"
                        }`}
                      >
                        {visitNote[s.key] ?? "Not recorded."}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="rounded-md border border-cream-200 p-3 text-sm text-muted dark:border-dark-700">
                    No SOAP notes recorded for this visit.
                  </p>
                )}
              </div>
            )}
            {/* Right — doctor's input, editable AI context, and the AI actions below. */}
            <div className="space-y-3">
              <div>
                <Label>Doctor's input</Label>
                <Textarea value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} rows={4} placeholder="Outline the plan for this referral reply — this is sent to the AI along with the ticked SOAP sections, doctor, and clinic details to guide the generated letter." />
              </div>
              <div>
                <Label>AI context</Label>
                <Textarea value={form.aiContext} onChange={(e) => setForm({ ...form, aiContext: e.target.value })} rows={6} className="font-mono text-xs" placeholder="System prompt that guides the AI. Pre-filled from Settings → AI Prompts (or the saved letter); edit if needed." />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={doGenerateDraft} disabled={polish.isPending}><WandSparkles size={14} className="mr-1" /> Generate AI draft</Button>
                <Button variant="outline" size="sm" onClick={doPolish} disabled={polish.isPending || !form.body.trim()}><Sparkles size={14} className="mr-1" /> Polish</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Body</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} placeholder="Write or generate the referral reply body. Markdown supported: **bold**, - lists, # headings." />
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleSave} disabled={busy || !form.patientId} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Save size={14} className="mr-1" /> Save</Button>
        <Button variant="outline" onClick={handleSaveToR2} disabled={busy || !form.patientId}><Upload size={14} className="mr-1" /> Save to R2</Button>
        <Button variant="outline" onClick={handlePrint} disabled={busy || !form.patientId}><Printer size={14} className="mr-1" /> Print</Button>
        <Button variant="outline" onClick={handleSendWebhook} disabled={busy || !form.patientId || !webhookConfigured} title={!webhookConfigured ? "No webhook configured for Referral Reply — enable it in Settings → Integrations" : ""}><Send size={14} className="mr-1" /> Send Webhook</Button>
        <Button variant="outline" onClick={() => { if (selectedDoctor) printDoctorLabel({ name: selectedDoctor.name, designation: selectedDoctor.designation, clinicHospital: selectedDoctor.clinicHospital, address: selectedDoctor.address }); else toast.error("Select a referring doctor first."); }} disabled={!selectedDoctor} title={!selectedDoctor ? "Select a referring doctor first" : "Print a 60mm × 40mm label for the selected doctor"}><Printer size={14} className="mr-1" /> Print label</Button>
        <Button variant="ghost" onClick={() => navigate("/referrals")}>Cancel</Button>
      </div>
    </div>
  );
}
